import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { useT } from '@/i18n/labels';
import { usePastConversations } from '@/hooks/usePastConversations';
import { usePersistedFlag } from '@/hooks/usePersistedFlag';
import { buildHistoryRows, conversationHref } from '@/utils/contactHistory';
import type { Conversation } from '@/types';

/** localStorage key: whether the agent keeps the section open. Closed by default - see the component doc. */
export const PAST_SECTION_OPEN_KEY = 'ac-cp-past-open';

/**
 * The contact panel's "Past conversations": this customer's OTHER conversations, newest first, each one click from open
 * (ADR-033 C3-2). Closed conversations are the point of it.
 *
 * <b>Closed by default, free until opened.</b> The list is one server request per customer; an agent clicking down a queue
 * would otherwise cost one per row. Opening it persists like the panel's other sections.
 *
 * <b>The open conversation is not listed.</b> It is the one on screen. It still counts as a successor, so the row it
 * continues carries the "continued in a later conversation" mark even though the current one is not a row.
 *
 * <b>Rows open through the normal path.</b> A click navigates to `/conversations/<id>` (keeping the agent's `?folder=`); the
 * page reads the row through the same visibility door as every other, so a conversation this viewer may not read answers
 * 404 and shows the existing "not available" sentence. The list itself is already what the server allowed.
 */
export const PastConversations: React.FC<{ conv: Conversation }> = ({ conv }) => {
  const { t } = useT();
  const navigate = useNavigate();
  const { search } = useLocation();
  const [open, toggleOpen] = usePersistedFlag(PAST_SECTION_OPEN_KEY, false);
  const past = usePastConversations(conv.customerId, open);
  const fallbackTitle = t('ctx_row_conversation_fallback');

  const rows = useMemo(() => {
    const here = conv.sessionId.toLowerCase();
    const others = past.sessions.filter((s) => s.sessionId.toLowerCase() !== here);
    return buildHistoryRows([], others, fallbackTitle, [
      { sessionId: conv.sessionId, previousSessionId: conv.previousSessionId },
    ]);
  }, [past.sessions, conv.sessionId, conv.previousSessionId, fallbackTitle]);

  return (
    <div className="ac-ctx-sec" data-testid="cp-past">
      <button
        type="button"
        className="ac-ctx-sec-toggle"
        onClick={toggleOpen}
        aria-expanded={open}
        data-testid="cp-past-toggle"
      >
        <span className="ac-ctx-sec-title">{t('cp_past_conversations')}</span>
        <Icon name="chevron" size={14} />
      </button>

      {open && (
        <div className="cp-sec-body" data-testid="cp-past-body">
          {past.loading ? (
            <div className="ac-ctx-na" data-testid="cp-past-loading">
              {t('cp_past_loading')}
            </div>
          ) : past.failed ? (
            <div className="ac-ctx-na" role="alert" data-testid="cp-past-failed">
              {t('cp_past_failed')}{' '}
              <button type="button" className="ac-btn ghost sm" onClick={past.retry} data-testid="cp-past-retry">
                {t('cl_retry')}
              </button>
            </div>
          ) : rows.length === 0 && !past.hasMore ? (
            <div className="ac-ctx-na" data-testid="cp-past-empty">
              {t('cp_past_empty')}
            </div>
          ) : (
            rows.map((row) => (
              <button
                key={row.id}
                type="button"
                className="ac-ctx-row link"
                aria-label={t('contact_open_conversation')}
                onClick={() => navigate(conversationHref(row.sessionId!, search))}
                data-testid={`cp-past-row-${row.id}`}
              >
                <div className="ac-ctx-row-ico">
                  <Icon name={row.icon} size={15} />
                </div>
                <div className="ac-ctx-row-main">
                  <div className="ac-ctx-row-title" dir="auto">
                    {row.title}
                  </div>
                  <div className="ac-ctx-row-sub">{row.sub}</div>
                  {row.continuedIn && (
                    <div className="ac-ctx-row-link" data-testid={`cp-past-continued-in-${row.id}`}>
                      {t('cp_continued_in')}
                    </div>
                  )}
                  {row.continues && (
                    <div className="ac-ctx-row-link" data-testid={`cp-past-continues-${row.id}`}>
                      {t('cp_continues')}
                    </div>
                  )}
                </div>
              </button>
            ))
          )}

          {!past.loading && !past.failed && past.hasMore && (
            <button
              type="button"
              className="ac-btn ghost sm"
              onClick={past.loadMore}
              disabled={past.loadingMore}
              data-testid="cp-past-more"
              style={{ marginBlockStart: 8, width: '100%' }}
            >
              {t('contact_load_more')}
            </button>
          )}
          {past.moreFailed && (
            <div className="ac-ctx-na" role="alert" data-testid="cp-past-more-failed">
              {t('cp_past_failed')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
